import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxColor, OnyxIconName } from '@onyx/angular';
import {
  OnyxDataWidgetComponent,
  OnyxDatePipe,
  OnyxDistancePipe,
  OnyxIconComponent,
  OnyxMapRoutePointStatus,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { DictionaryCode } from '../../../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../../../common/helpers/common.helper';
import { DeadlineHelper } from '../../../../../../../common/helpers/deadline.helper';
import type { RoutePointData } from '../../../../../../../common/helpers/route.helper';
import { RouteHelper } from '../../../../../../../common/helpers/route.helper';
import { RoutePointValueHelper } from '../../../../../../../common/helpers/route-point-value.helper';
import type { TelematicsTechnicalRoutePoint } from '../../../../../../../common/interfaces/telematics/telematics-route-point';
import { DiffPipe } from '../../../../../../../common/pipes/diff.pipe';
import { DurationPipe } from '../../../../../../../common/pipes/duration.pipe';
import { TelematicsStore } from '../../../../../../../common/stores/telematics.store';
import { OrderPointStatus } from '../../../../../common/enums/order-point-status';
import { OrderStatus } from '../../../../../common/enums/order-status';
import { OrderHelper } from '../../../../../common/helpers/order.helper';
import type { Order } from '../../../../../common/interfaces/order';
import type { OrderPoint } from '../../../../../common/interfaces/order-point';

@Component({
  selector: 'app-order-modal-realization-status',
  imports: [
    OnyxDataWidgetComponent,
    TranslatePipe,
    OnyxDatePipe,
    DiffPipe,
    DurationPipe,
    OnyxTooltipDirective,
    OnyxDistancePipe,
    OnyxIconComponent,
    AsyncPipe,
    NgTemplateOutlet,
  ],
  templateUrl: './order-modal-realization-status.component.html',
  styleUrl: './order-modal-realization-status.component.scss',
})
export class OrderModalRealizationStatusComponent {
  protected readonly commonHelper = inject(CommonHelper);
  private readonly telematicsStore = inject(TelematicsStore);
  private readonly translateService = inject(TranslateService);
  private readonly orderHelper = inject(OrderHelper);

  public readonly order = input.required<Order>();
  public readonly currentOrderPoint = input.required<OrderPoint>();
  public readonly currentSwapPoint =
    input.required<TelematicsTechnicalRoutePoint | null>();
  public readonly pointData = input.required<RoutePointData | null>();
  public readonly color = input.required<OnyxColor | null>();

  protected readonly OrderPointStatus = OrderPointStatus;
  protected readonly DictionaryCode = DictionaryCode;

  protected currentPoint = computed(
    () => this.currentSwapPoint()?.technicalPoint ?? this.currentOrderPoint(),
  );
  protected orderStatus = computed(() => this.order().status.value);
  protected pointStatus = computed(() => this.currentPoint().status);

  protected isOrderInProgress = computed(
    () => this.orderStatus() === OrderStatus.IN_PROGRESS,
  );
  protected isPointToDo = computed(
    () => this.pointStatus() === OrderPointStatus.TO_DO,
  );

  protected telematicsItem = computed(() =>
    this.telematicsStore.getItem(this.order().assignedData?.vehicle?.uuid),
  );
  protected estimations = computed(() =>
    RoutePointValueHelper.getEstimations(this.currentPoint()),
  );
  protected action = computed(() =>
    RouteHelper.getPointAction(
      this.currentPoint(),
      this.currentPoint()?.status,
      this.pointData()?.compliance,
      true,
      this.translateService,
    ),
  );
  protected deadline = computed(() =>
    DeadlineHelper.getPointDeadline(this.currentPoint()),
  );

  protected icon = computed(() => {
    const DEFAULT_ICON = 'clock-filled' satisfies OnyxIconName;

    const name = (() => {
      if (this.orderStatus() === OrderStatus.TO_DO) return DEFAULT_ICON;

      if (this.currentPoint().status === OnyxMapRoutePointStatus.TO_DO) {
        return 'vehicle-ride-filled' satisfies OnyxIconName;
      }

      return this.action()?.icon ?? DEFAULT_ICON;
    })();

    return {
      name,
      tooltip: this.isOrderInProgress() ? this.action()?.tooltip : undefined,
    };
  });

  protected pointTimeWindows = computed(() =>
    this.orderHelper.getTimeWindows(this.currentPoint(), {
      businessHoursFormat: 'short',
      showSameDay: true,
    }),
  );

  protected realizationData = computed(() => {
    if (this.order().outsourcingData) {
      return { label: 'labels.outsourcing', time: null, displayTime: null };
    }

    const currentPoint = this.currentPoint();
    if (
      this.pointStatus() === OrderPointStatus.TO_DO &&
      !currentPoint.estimations?.arrivalTime
    ) {
      return { label: 'labels.unknownEta', time: null, displayTime: null };
    }

    const label =
      (
        {
          [OrderPointStatus.TO_DO]: 'labels.eta',
          [OrderPointStatus.IN_PROGRESS]: 'labels.from',
          [OrderPointStatus.WAITING]: 'labels.from',
        } as Partial<Record<OrderPointStatus, string>>
      )[currentPoint.status] ?? '';

    return {
      label,
      time: RouteHelper.getPointRealizationTime(currentPoint, {
        includeServiceTime: false,
        compareToNow: false,
      }),
    };
  });
}
